import { Settings04, Star06, Percent03, ShoppingCart03, Rows03 } from '@untitledui/icons'
import { type ReactElement, useState } from 'react'
import { twJoin } from 'tailwind-merge'

import Button from '@components/designSystem/Button'
import ButtonGroup, { type RadioEntry } from '@components/designSystem/ButtonGroup'
import Dialog from '@components/designSystem/Dialog/Dialog'
import { RadioButtonBase } from '@components/designSystem/Radio'
import useTypedTranslation from '@hooks/useTypedTranslation'

import type { BaseDialogProps } from '@components/designSystem/Dialog/DialogContext'
import type { FilterAndSort, FilterAndSortWrapper } from '@state/util/todoList'

interface FilterAndSortInterfaceProps extends BaseDialogProps {
  filterAndSort: FilterAndSort
  updateFilterAndSort: (source: Partial<FilterAndSortWrapper>) => void
}

function FilterAndSortDialog({ filterAndSort, updateFilterAndSort, onClose }: FilterAndSortInterfaceProps) {
  const [filterAndSortCopy, setFilterAndSortCopy] = useState({ ...filterAndSort })
  const { t } = useTypedTranslation()

  function update(source: Partial<FilterAndSort>) {
    const updated = { ...filterAndSortCopy, ...source }
    setFilterAndSortCopy(updated)
  }

  function save() {
    updateFilterAndSort(filterAndSortCopy)
    onClose?.()
  }

  const sortEntries: RadioEntry<FilterAndSort['sort']>[] = [
    { label: t('todoList.filterAndSort.sortOptions.newestFirst'), value: 'NEW' },
    { label: t('todoList.filterAndSort.sortOptions.oldestFirst'), value: 'NEW-' },
    { label: t('todoList.filterAndSort.sortOptions.advertisementsFirst'), value: 'ADVERTISEMENT', disabled: true },
    { label: t('todoList.filterAndSort.sortOptions.alphabetical'), value: 'ALPHA' },
    { label: t('todoList.filterAndSort.sortOptions.shelf'), value: 'SHELF' },
  ] as const

  const divider = <div className='h-px w-full bg-gray-200' />

  return (
    <Dialog
      headerIcon={<Settings04 />}
      title={t('todoList.filterAndSort.title')}
      onClose={onClose}
      testId="filterAndSortDialog"
    >
      <h2 className='mb-2 text-md font-medium'>{t('todoList.filterAndSort.sortBy')}</h2>

      <ButtonGroup
        entries={sortEntries}
        onChange={sort => update({ sort })}
        selectedValue={filterAndSortCopy.sort}
        className='mb-8 flex-wrap'
      />

      <h2 className='mb-2 text-md font-medium'>{t('todoList.filterAndSort.filter')}</h2>

      <div className='mb-8 flex flex-col gap-1 sm:gap-5'>
        <FilterOption
          icon={<Rows03 />}
          title={t('todoList.filterAndSort.options.all.title')}
          description={t('todoList.filterAndSort.options.all.description')}
          isSelected={filterAndSortCopy.secondaryFilter === 'NONE'}
          onSelect={() => update({ secondaryFilter: 'NONE' })}
        />

        {divider}

        <FilterOption
          icon={<Star06 />}
          title={t('todoList.filterAndSort.options.newTasks.title')}
          description={t('todoList.filterAndSort.options.newTasks.description')}
          isSelected={filterAndSortCopy.secondaryFilter === 'NEW'}
          onSelect={() => update({ secondaryFilter: 'NEW' })}
        />

        {divider}

        <FilterOption
          icon={<Percent03 />}
          title={t('todoList.filterAndSort.options.promotionalProducts.title')}
          description={t('todoList.filterAndSort.options.promotionalProducts.description')}
          isSelected={filterAndSortCopy.secondaryFilter === 'ADVERTISEMENT'}
          onSelect={() => update({ secondaryFilter: 'ADVERTISEMENT' })}
          disabled={true}
        />

        {divider}

        <FilterOption
          icon={<ShoppingCart03 />}
          title={t('todoList.filterAndSort.options.manualReorders.title')}
          description={t('todoList.filterAndSort.options.manualReorders.description')}
          isSelected={filterAndSortCopy.secondaryFilter === 'MANUAL_ORDER'}
          onSelect={() => update({ secondaryFilter: 'MANUAL_ORDER' })}
          disabled={true}
        />
      </div>

      <Button
        variant='filled'
        className='w-full'
        onClick={save}
      >
        {t('common.save')}
      </Button>
    </Dialog>
  )
}

interface FilterOptionProps {
  icon: ReactElement
  title: string
  description: string
  isSelected: boolean
  onSelect: () => void
  disabled?: boolean
}

function FilterOption({ icon, title, description, isSelected, onSelect, disabled = false }: FilterOptionProps) {
  return (
    <div
      className={twJoin(
        'flex items-start gap-4',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      )}
      onClick={disabled ? undefined : onSelect}
    >
      <div className=''>
        {icon}
      </div>
      <div className='flex-1'>
        <h3 className='text-sm font-medium'>{title}</h3>
        <p className='text-xs text-secondary'>{description}</p>
      </div>
      <div className=''>
        <RadioButtonBase isSelected={isSelected} />
      </div>
    </div>
  )
}

export default FilterAndSortDialog